import { getActiveLocale, t } from '../i18n/translate';
import {
  FirstStepSignupAssignment,
  FirstStepSignupConfig,
  FirstStepSlotKind,
} from '../types/workshopFirstStepSignup';

/** Presenters → Alternates → Outside (outside appended; does not shift existing indexes). */
export function totalSignupColumns(config: FirstStepSignupConfig): number {
  return (
    config.standard_slot_count +
    config.alternate_slot_count +
    Math.max(0, config.outside_slot_count || 0)
  );
}

export function slotKindForColumn(
  config: FirstStepSignupConfig,
  columnIndex: number
): FirstStepSlotKind {
  if (columnIndex < config.standard_slot_count) return 'standard';
  if (columnIndex < config.standard_slot_count + config.alternate_slot_count) return 'alternate';
  return 'outside';
}

export function columnHeaders(config: FirstStepSignupConfig): string[] {
  const std = Array.from({ length: config.standard_slot_count }, (_, i) =>
    config.standard_slot_count === 1
      ? t('workshop.2026.first_step.col_presenter')
      : t('workshop.2026.first_step.col_presenter_n', { n: i + 1 })
  );
  const alt = Array.from({ length: config.alternate_slot_count }, (_, i) =>
    config.alternate_slot_count === 1
      ? t('workshop.2026.first_step.col_alternate')
      : t('workshop.2026.first_step.col_alternate_n', { n: i + 1 })
  );
  const outsideCount = Math.max(0, config.outside_slot_count || 0);
  const outside = Array.from({ length: outsideCount }, (_, i) =>
    outsideCount === 1
      ? t('workshop.2026.first_step.col_outside')
      : t('workshop.2026.first_step.col_outside_n', { n: i + 1 })
  );
  return [...std, ...alt, ...outside];
}

export function columnHeaderFull(config: FirstStepSignupConfig, columnIndex: number): string {
  const kind = slotKindForColumn(config, columnIndex);
  if (kind === 'standard') {
    const n = columnIndex + 1;
    return config.standard_slot_count === 1
      ? t('workshop.2026.first_step.col_presenter')
      : t('workshop.2026.first_step.col_presenter_n', { n });
  }
  if (kind === 'alternate') {
    const n = columnIndex - config.standard_slot_count + 1;
    return config.alternate_slot_count === 1
      ? t('workshop.2026.first_step.col_alternate')
      : t('workshop.2026.first_step.col_alternate_n', { n });
  }
  const n = columnIndex - config.standard_slot_count - config.alternate_slot_count + 1;
  const outsideCount = Math.max(0, config.outside_slot_count || 0);
  return outsideCount === 1
    ? t('workshop.2026.first_step.col_outside_full')
    : t('workshop.2026.first_step.col_outside_n', { n });
}

export type FirstStepSlotBand = {
  kind: FirstStepSlotKind;
  title: string;
  shortTitle: string;
  columnIndexes: number[];
};

/** Grouped bands for Option A layout (skips empty outside when count is 0). */
export function slotBands(config: FirstStepSignupConfig): FirstStepSlotBand[] {
  const bands: FirstStepSlotBand[] = [
    {
      kind: 'standard',
      title: t('workshop.2026.first_step.band_in'),
      shortTitle: t('workshop.2026.first_step.short_present'),
      columnIndexes: Array.from({ length: config.standard_slot_count }, (_, i) => i),
    },
    {
      kind: 'alternate',
      title: t('workshop.2026.first_step.band_alts'),
      shortTitle: t('workshop.2026.first_step.short_alt'),
      columnIndexes: Array.from(
        { length: config.alternate_slot_count },
        (_, i) => config.standard_slot_count + i
      ),
    },
  ];
  const outsideCount = Math.max(0, config.outside_slot_count || 0);
  if (outsideCount > 0) {
    const start = config.standard_slot_count + config.alternate_slot_count;
    bands.push({
      kind: 'outside',
      title: t('workshop.2026.first_step.band_outside'),
      shortTitle: t('workshop.2026.first_step.short_outside'),
      columnIndexes: Array.from({ length: outsideCount }, (_, i) => start + i),
    });
  }
  return bands;
}

export function assignmentAt(
  config: FirstStepSignupConfig,
  weekIndex: number,
  columnIndex: number
): FirstStepSignupAssignment | undefined {
  return config.assignments.find(
    (a) => a.week_index === weekIndex && a.column_index === columnIndex
  );
}

export function myAssignment(
  config: FirstStepSignupConfig,
  viewerUserId?: string
): FirstStepSignupAssignment | undefined {
  return config.assignments.find(
    (a) => a.is_you || (viewerUserId != null && a.user_id === viewerUserId)
  );
}

export function weekPresentationDate(
  config: FirstStepSignupConfig,
  weekIndex: number
): string | null {
  const weekMeta = config.weeks?.find((w) => w.week_index === weekIndex);
  if (weekMeta?.presentation_date) return weekMeta.presentation_date;

  const first = (config.first_presentation_week_date || '').trim();
  if (!first || weekIndex < 1) return null;
  const [y, m, d] = first.split('-').map(Number);
  if (!y || !m || !d) return null;
  const start = new Date(y, m - 1, d);
  if (Number.isNaN(start.getTime())) return null;
  start.setDate(start.getDate() + 7 * (weekIndex - 1));
  const yy = start.getFullYear();
  const mm = String(start.getMonth() + 1).padStart(2, '0');
  const dd = String(start.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

/** Parse YYYY-MM-DD as a local calendar date (noon-safe via local Y/M/D). */
function parseLocalIsoDate(iso: string): Date | null {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return null;
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? null : date;
}

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function addLocalDays(d: Date, days: number): Date {
  const next = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  next.setDate(next.getDate() + days);
  return next;
}

export type FirstStepWeekTemporalPhase = 'past' | 'current' | 'future';

/**
 * Past / current / future for First Step week cards.
 * Current window: day after previous week's meeting through this week's meeting day
 * (inclusive). Week 1 uses the 7 days ending on its meeting day when there is no prior week.
 */
export function weekTemporalPhase(
  config: FirstStepSignupConfig,
  weekIndex: number,
  today: Date = new Date()
): FirstStepWeekTemporalPhase {
  const meetingIso = weekPresentationDate(config, weekIndex);
  if (!meetingIso) return 'future';

  const meetingDay = parseLocalIsoDate(meetingIso);
  if (!meetingDay) return 'future';

  const todayDay = startOfLocalDay(today);
  if (todayDay.getTime() > meetingDay.getTime()) return 'past';

  let windowStart: Date;
  if (weekIndex > 1) {
    const prevIso = weekPresentationDate(config, weekIndex - 1);
    const prevDay = prevIso ? parseLocalIsoDate(prevIso) : null;
    windowStart = prevDay ? addLocalDays(prevDay, 1) : addLocalDays(meetingDay, -6);
  } else {
    windowStart = addLocalDays(meetingDay, -6);
  }

  if (todayDay.getTime() >= windowStart.getTime() && todayDay.getTime() <= meetingDay.getTime()) {
    return 'current';
  }
  return 'future';
}

export function formatPresentationDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(getActiveLocale() || undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function weekLockedForMember(
  config: FirstStepSignupConfig,
  weekIndex: number
): boolean {
  const week = config.weeks?.find((w) => w.week_index === weekIndex);
  if (week) return week.locked_for_member;
  if (config.locked_week_indexes.includes(weekIndex)) return true;
  return false;
}

export function isAlternateSlot(config: FirstStepSignupConfig, columnIndex: number): boolean {
  return slotKindForColumn(config, columnIndex) === 'alternate';
}

export function isOutsideSlot(config: FirstStepSignupConfig, columnIndex: number): boolean {
  return slotKindForColumn(config, columnIndex) === 'outside';
}

function roleLabel(kind: FirstStepSlotKind): string {
  if (kind === 'alternate') return t('workshop.2026.first_step.role_alternate');
  if (kind === 'outside') return t('workshop.2026.first_step.role_outside');
  return t('workshop.2026.first_step.role_presenter');
}

export function firstStepSignupCollapsedSummary(
  config: FirstStepSignupConfig | null,
  loading: boolean,
  viewerUserId?: string
): string {
  if (loading) return t('workshop.2026.first_step.summary_loading');
  if (!config) return t('workshop.2026.first_step.summary_not_open');
  if (!config.enabled && config.assignments.length === 0) {
    return t('workshop.2026.first_step.summary_not_open');
  }

  const mine = myAssignment(config, viewerUserId);
  if (!mine) return t('workshop.2026.first_step.summary_not_signed');

  const role = roleLabel(slotKindForColumn(config, mine.column_index));
  const date = formatPresentationDate(weekPresentationDate(config, mine.week_index));
  if (date) {
    return t('workshop.2026.first_step.summary_signed_dated', {
      role,
      week: mine.week_index,
      date,
    });
  }
  return t('workshop.2026.first_step.summary_signed', {
    role,
    week: mine.week_index,
  });
}

export function firstStepSignupShouldShow(
  config: FirstStepSignupConfig | null,
  _options?: { canFacilitate?: boolean }
): boolean {
  return !!config?.enabled;
}

export function firstStepSignupStatusLine(
  config: FirstStepSignupConfig,
  viewerUserId?: string
): string | null {
  const mine = myAssignment(config, viewerUserId);
  if (!mine) return null;
  const kind = slotKindForColumn(config, mine.column_index);
  if (kind === 'alternate') {
    return t('workshop.2026.first_step.status_alternate', { week: mine.week_index });
  }
  if (kind === 'outside') {
    return t('workshop.2026.first_step.status_outside', { week: mine.week_index });
  }
  return t('workshop.2026.first_step.status_presenter', { week: mine.week_index });
}
